import { useCallback, useMemo, useState } from 'react';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';

import type {
  RemittanceSource,
  RemittanceStatus,
} from '../services/api/types.gen';
import { useStore } from '../store';
import {
  DEFAULT_REMITTANCE_STATUS_TAB,
  REMITTANCE_DEFAULT_SORTING,
  REMITTANCE_STATUS_TAB_IDS,
  SUPPORTED_REMITTANCE_SORT_IDS,
} from '../store/slices/billingUiSlice';
import { formatDateToISO, parseISOToLocalDate } from '../utils/dateUtils';

/** Server request params produced from the remittance query state. Empty values are
 *  `undefined` (never '' / [] / null) so they are omitted from the request URL and
 *  `JSON.stringify(params)` stays a stable fetch key. */
export interface RemittanceTableQueryParams {
  search: string | undefined;
  sort_by: string | undefined;
  sort_order: 'asc' | 'desc' | undefined;
  /** 1-based page index for the backend. */
  page: number;
  per_page: number;
  status: RemittanceStatus[] | undefined;
  source: RemittanceSource[] | undefined;
  payor_id: string[] | undefined;
  /** ISO 'YYYY-MM-DD'. */
  deposit_date_from: string | undefined;
  /** ISO 'YYYY-MM-DD'. */
  deposit_date_to: string | undefined;
}

/**
 * Owns the Remittances view's server-driven query state.
 *
 * Persisted fields (`globalFilter`/`statusTab`/`sources`/`payorIds`/deposit dates/
 * `sorting`/`pageSize`) live in the `billingUiSlice`; the non-persisted `pageIndex`
 * lives here and resets to 0 on any non-page change to avoid stale-page requests.
 */
export interface RemittanceTableQuery {
  search: string;
  sorting: SortingState;
  /** 0-based page index (tanstack convention). */
  page: number;
  perPage: number;
  /** The selected status tab. Not a filter: `reset()` leaves it alone, the same
   *  way resetting a claims tab's toolbar does not send you back to tab one. */
  statusTab: RemittanceStatus;
  sources: RemittanceSource[];
  payorIds: string[];
  /** Parsed for DatePicker; persisted as ISO strings. */
  depositDateFrom: Date | undefined;
  depositDateTo: Date | undefined;
  setSearch: (value: string) => void;
  /** Single-sort: accepts a value or a tanstack updater; only sorting[0] is kept. */
  setSorting: OnChangeFn<SortingState>;
  setPage: (pageIndex: number) => void;
  setPerPage: (perPage: number) => void;
  setStatusTab: (value: RemittanceStatus) => void;
  setSources: (values: RemittanceSource[]) => void;
  setPayorIds: (ids: string[]) => void;
  setDepositDateFrom: (d: Date | undefined) => void;
  setDepositDateTo: (d: Date | undefined) => void;
  reset: () => void;
  toParams: () => RemittanceTableQueryParams;
}

export function useRemittanceTableQuery(): RemittanceTableQuery {
  const ui = useStore(state => state.remittanceUiState);
  const setUi = useStore(state => state.setRemittanceUi);
  const resetUi = useStore(state => state.resetRemittanceUi);
  const [pageIndex, setPageIndex] = useState(0);

  // Sanitized here rather than in the store so the persisted field keeps a stable
  // reference (see the DEFAULT_REMITTANCE_UI comment in billingUiSlice).
  const sorting = useMemo<SortingState>(() => {
    const first = ui.sorting?.[0];
    return first && SUPPORTED_REMITTANCE_SORT_IDS.has(first.id)
      ? [first]
      : REMITTANCE_DEFAULT_SORTING;
  }, [ui.sorting]);

  // Sanitized for the same reason as `sorting`, and because a payload persisted
  // before the status tab existed rehydrates with no `statusTab` at all. Sending
  // `status=undefined` would blank the table and leave every tab inactive, so an
  // unknown/missing value falls back to the head of the pipeline.
  const statusTab = useMemo<RemittanceStatus>(
    () =>
      REMITTANCE_STATUS_TAB_IDS.has(ui.statusTab)
        ? ui.statusTab
        : DEFAULT_REMITTANCE_STATUS_TAB,
    [ui.statusTab]
  );

  const setSearch = useCallback(
    (value: string) => {
      setPageIndex(0);
      setUi({ globalFilter: value });
    },
    [setUi]
  );

  const setSorting = useCallback<OnChangeFn<SortingState>>(
    updater => {
      setPageIndex(0);
      const resolved =
        typeof updater === 'function' ? updater(sorting) : updater;
      // The backend is single-sort: keep only the first sort entry. Clearing the
      // sort (TanStack's third header click) would send no sort_by, which the
      // backend reads as received_at DESC — not the view's default — so restore
      // the default sort instead of an empty sort.
      setUi({
        sorting:
          resolved.length > 0 ? [resolved[0]] : REMITTANCE_DEFAULT_SORTING,
      });
    },
    [setUi, sorting]
  );

  const setPage = useCallback((index: number) => {
    setPageIndex(index);
  }, []);

  const setPerPage = useCallback(
    (perPage: number) => {
      setPageIndex(0);
      setUi({ pageSize: perPage });
    },
    [setUi]
  );

  const setStatusTab = useCallback(
    (value: RemittanceStatus) => {
      setPageIndex(0);
      setUi({ statusTab: value });
    },
    [setUi]
  );

  const setSources = useCallback(
    (values: RemittanceSource[]) => {
      setPageIndex(0);
      setUi({ sources: values });
    },
    [setUi]
  );

  const setPayorIds = useCallback(
    (ids: string[]) => {
      setPageIndex(0);
      setUi({ payorIds: ids });
    },
    [setUi]
  );

  const setDepositDateFrom = useCallback(
    (d: Date | undefined) => {
      setPageIndex(0);
      setUi({ depositDateFrom: d ? formatDateToISO(d) : null });
    },
    [setUi]
  );

  const setDepositDateTo = useCallback(
    (d: Date | undefined) => {
      setPageIndex(0);
      setUi({ depositDateTo: d ? formatDateToISO(d) : null });
    },
    [setUi]
  );

  const reset = useCallback(() => {
    setPageIndex(0);
    resetUi();
  }, [resetUi]);

  const toParams = useCallback((): RemittanceTableQueryParams => {
    const sort = sorting[0];
    return {
      search: ui.globalFilter || undefined,
      sort_by: sort ? sort.id : undefined,
      sort_order: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
      page: pageIndex + 1,
      per_page: ui.pageSize,
      // The tab is always one concrete status, so this is never absent — the
      // list is never unfiltered by status.
      status: [statusTab],
      source: ui.sources.length ? ui.sources : undefined,
      payor_id: ui.payorIds.length ? ui.payorIds : undefined,
      deposit_date_from: ui.depositDateFrom ?? undefined,
      deposit_date_to: ui.depositDateTo ?? undefined,
    };
  }, [ui, sorting, statusTab, pageIndex]);

  return {
    search: ui.globalFilter,
    sorting,
    page: pageIndex,
    perPage: ui.pageSize,
    statusTab,
    sources: ui.sources,
    payorIds: ui.payorIds,
    depositDateFrom: parseISOToLocalDate(ui.depositDateFrom),
    depositDateTo: parseISOToLocalDate(ui.depositDateTo),
    setSearch,
    setSorting,
    setPage,
    setPerPage,
    setStatusTab,
    setSources,
    setPayorIds,
    setDepositDateFrom,
    setDepositDateTo,
    reset,
    toParams,
  };
}
